// Competition management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';
import { generateInviteCode } from '../utils/inviteCode';

// Create a new competition
export const createCompetition = async (competitionData, organizerId, leagueId = null) => {
  const competitionId = uuidv4();
  const competitionRef = doc(db, 'competitions', competitionId);

  const competition = {
    name: competitionData.name,
    startDate: competitionData.startDate,
    endDate: competitionData.endDate,
    maxPlayers: competitionData.maxPlayers || 20,
    timezone: competitionData.timezone || 'Europe/Oslo',
    rankingMethod: competitionData.rankingMethod || 'byCategory',
    inviteCode: generateInviteCode(),
    organizerId,
    leagueId, // Link to league (null for standalone competitions)
    votingEnabled: competitionData.votingEnabled || false, // Enable game voting
    votingDeadline: competitionData.votingDeadline || null, // Deadline for voting
    status: 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(competitionRef, competition);

  return {
    id: competitionId,
    ...competition
  };
};

// Get competition by ID
export const getCompetition = async (competitionId) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  const competitionSnap = await getDoc(competitionRef);

  if (!competitionSnap.exists()) {
    return null;
  }

  return {
    id: competitionSnap.id,
    ...competitionSnap.data()
  };
};

// Get competition by invite code
export const getCompetitionByInviteCode = async (inviteCode) => {
  const competitionsRef = collection(db, 'competitions');
  const q = query(competitionsRef, where('inviteCode', '==', inviteCode.toUpperCase()));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return null;
  }

  const competitionDoc = querySnapshot.docs[0];
  return {
    id: competitionDoc.id,
    ...competitionDoc.data()
  };
};

// Update competition
export const updateCompetition = async (competitionId, updates) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  await updateDoc(competitionRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Change competition status
export const updateCompetitionStatus = async (competitionId, status) => {
  await updateCompetition(competitionId, { status });
};

// Regenerate invite code
export const regenerateInviteCode = async (competitionId) => {
  const newCode = generateInviteCode();
  await updateCompetition(competitionId, { inviteCode: newCode });
  return newCode;
};

// Transfer organizer role
export const transferOrganizer = async (competitionId, newOrganizerId) => {
  await updateCompetition(competitionId, { organizerId: newOrganizerId });
};

// Get all competitions for organizer
export const getCompetitionsForOrganizer = async (organizerId) => {
  const competitionsRef = collection(db, 'competitions');
  const q = query(competitionsRef, where('organizerId', '==', organizerId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update celebration photo URL
export const updateCelebrationPhoto = async (competitionId, photoUrl) => {
  await updateCompetition(competitionId, { celebrationPhotoUrl: photoUrl });
};

// Remove celebration photo
export const removeCelebrationPhoto = async (competitionId) => {
  await updateCompetition(competitionId, { celebrationPhotoUrl: null });
};

// Get all competitions for a league
export const getCompetitionsForLeague = async (leagueId) => {
  const competitionsRef = collection(db, 'competitions');
  const q = query(competitionsRef, where('leagueId', '==', leagueId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Link existing competition to a league
export const linkCompetitionToLeague = async (competitionId, leagueId) => {
  await updateCompetition(competitionId, { leagueId });
};

// Delete competition and all related data
// Only allowed if there's only 1 active player
export const deleteCompetition = async (competitionId) => {
  // Get all players
  const playersRef = collection(db, 'players');
  const playersQuery = query(playersRef, where('competitionId', '==', competitionId));
  const playersSnap = await getDocs(playersQuery);
  const players = playersSnap.docs;

  // Check if there's more than 1 active player
  const activePlayers = players.filter(p => p.data().status === 'active');
  if (activePlayers.length > 1) {
    throw new Error(`Cannot delete competition with ${activePlayers.length} active players. Only competitions with 1 player can be deleted.`);
  }

  // Delete all players
  for (const playerDoc of players) {
    await deleteDoc(playerDoc.ref);
  }

  // Delete all categories
  const categoriesRef = collection(db, 'categories');
  const categoriesQuery = query(categoriesRef, where('competitionId', '==', competitionId));
  const categoriesSnap = await getDocs(categoriesQuery);
  for (const catDoc of categoriesSnap.docs) {
    await deleteDoc(catDoc.ref);
  }

  // Delete all games
  const gamesRef = collection(db, 'games');
  const gamesQuery = query(gamesRef, where('competitionId', '==', competitionId));
  const gamesSnap = await getDocs(gamesQuery);
  for (const gameDoc of gamesSnap.docs) {
    await deleteDoc(gameDoc.ref);
  }

  // Delete all results
  const resultsRef = collection(db, 'results');
  const resultsQuery = query(resultsRef, where('competitionId', '==', competitionId));
  const resultsSnap = await getDocs(resultsQuery);
  for (const resultDoc of resultsSnap.docs) {
    await deleteDoc(resultDoc.ref);
  }

  // Delete all playerTokens
  const tokensRef = collection(db, 'playerTokens');
  const tokensQuery = query(tokensRef, where('competitionId', '==', competitionId));
  const tokensSnap = await getDocs(tokensQuery);
  for (const tokenDoc of tokensSnap.docs) {
    await deleteDoc(tokenDoc.ref);
  }

  // Delete name index subcollection
  const nameIndexRef = collection(db, 'competitions', competitionId, 'nameIndex');
  const nameIndexSnap = await getDocs(nameIndexRef);
  for (const nameDoc of nameIndexSnap.docs) {
    await deleteDoc(nameDoc.ref);
  }

  // Finally delete the competition itself
  const competitionRef = doc(db, 'competitions', competitionId);
  await deleteDoc(competitionRef);

  return true;
};
